// Path: src/components/permissions/ResourceSidebar.tsx
import React, { useState, useEffect } from 'react';
// 引用之前提取的共享类型
import { type CourseNode, type ResourceContext } from '../../types/permissionTypes';
import { getCourseStages, getStageLessons } from '../../lib/api';
import { type StageNode, type LessonNode } from '../../pages/UserLessonsPage';

interface Props {
  courses: CourseNode[];
  selectedNode: ResourceContext | null;
  onSelect: (node: ResourceContext) => void;
}

const ResourceSidebar: React.FC<Props> = ({ courses, selectedNode, onSelect }) => {
  // 存储带有子节点的课程数据
  const [enrichedCourses, setEnrichedCourses] = useState<CourseNode[]>(courses);
  // 存储展开节点的 Key集合 (格式: "course-1", "stage-2")
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  // 存储正在加载的节点 Key集合
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());

  // 当父组件传递的courses变化时，更新本地状态
  useEffect(() => {
    setEnrichedCourses(courses);
  }, [courses]);

  // 切换展开/折叠状态，并在需要时调用API获取子资源
  const toggleExpand = async (e: React.MouseEvent, key: string) => {
    e.stopPropagation(); // 防止冒泡触发 Select
    const [type, id] = key.split('-');
    const resourceId = parseInt(id, 10);
    const newSet = new Set(expandedKeys);
    const isExpanded = newSet.has(key);

    if (isExpanded) {
      // 如果已经展开，则折叠
      newSet.delete(key);
      setExpandedKeys(newSet);
      return;
    }

    // 如果是课程，获取其阶段
    if (type === 'course') {
      const course = enrichedCourses.find(c => c.id === resourceId);
      // 如果课程没有阶段或阶段为空，调用API获取
      if (!course?.stages || course.stages.length === 0) {
        setLoadingKeys(prev => new Set(prev).add(key));
        try {
          const response = await getCourseStages(resourceId);
          const stages = response.data.map((stage: any) => ({
            id: stage.id,
            title: stage.title,
            lessons: []
          }));
          
          // 更新课程的阶段
          setEnrichedCourses(prev => prev.map(c => 
            c.id === resourceId ? { ...c, stages } : c
          ));
        } catch (error) {
          console.error('Failed to fetch stages:', error);
        } finally {
          setLoadingKeys(prev => {
            const newLoading = new Set(prev);
            newLoading.delete(key);
            return newLoading;
          });
        }
      }
    } 
    // 如果是阶段，获取其课时
    else if (type === 'stage') {
      // 找到对应的阶段
      let updatedCourses = [...enrichedCourses];
      let targetStage: any = null;
      let courseIndex = -1;
      let stageIndex = -1;

      for (let i = 0; i < updatedCourses.length; i++) {
        const course = updatedCourses[i];
        if (course.stages) {
          const stage = course.stages.find((s: any, idx: number) => {
            stageIndex = idx;
            return s.id === resourceId;
          });
          if (stage) {
            targetStage = stage;
            courseIndex = i;
            break;
          }
        }
      }

      // 如果阶段存在且没有课时或课时为空，调用API获取
      if (targetStage && (!targetStage.lessons || targetStage.lessons.length === 0)) {
        setLoadingKeys(prev => new Set(prev).add(key));
        try {
          const response = await getStageLessons(resourceId);
          const lessons = response.data.map((lesson: any) => ({
            id: lesson.id,
            title: lesson.title,
            sort_order: lesson.sort_order
          }));
          
          // 更新阶段的课时
          if (courseIndex !== -1 && stageIndex !== -1) {
            updatedCourses[courseIndex] = {
              ...updatedCourses[courseIndex],
              stages: [...updatedCourses[courseIndex].stages!].map((stage: any, idx: number) => 
                idx === stageIndex ? { ...stage, lessons } : stage
              )
            };
            setEnrichedCourses(updatedCourses);
          }
        } catch (error) {
          console.error('Failed to fetch lessons:', error);
        } finally {
          setLoadingKeys(prev => {
            const newLoading = new Set(prev);
            newLoading.delete(key);
            return newLoading;
          });
        }
      }
    }

    // 展开节点
    newSet.add(key);
    setExpandedKeys(newSet);
  };

  // 辅助函数：判断是否选中
  const isSelected = (type: string, id: number) => {
    return selectedNode?.type === type && selectedNode.id === id;
  };

  return (
    <div className="p-4 h-full flex flex-col">
      <h2 className="font-semibold text-gray-700 mb-4 sticky top-0 bg-white pb-2 border-b z-10 flex justify-between items-center">
        <span>资源架构</span>
        <button 
          onClick={() => setExpandedKeys(new Set())}
          className="text-xs text-gray-400 hover:text-blue-600 font-normal"
        >
          全部折叠
        </button>
      </h2>
      
      <div className="space-y-1 flex-1 overflow-y-auto pr-2 custom-scrollbar">
        {enrichedCourses.map(course => {
          const courseKey = `course-${course.id}`;
          const isCourseExpanded = expandedKeys.has(courseKey);
          const hasStages = course.stages && course.stages.length > 0;
          const isCourseLoading = loadingKeys.has(courseKey);

          return (
            <div key={course.id}>
              {/* ================= LEVEL 1: COURSE ================= */}
              <div 
                onClick={() => onSelect({ type: 'course', id: course.id, title: course.title })}
                className={`group cursor-pointer p-2 rounded flex items-center transition-all duration-200 select-none ${
                  isSelected('course', course.id)
                  ? 'bg-blue-50 text-blue-700 font-medium shadow-sm ring-1 ring-blue-100' 
                  : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                {/* 展开/折叠按钮 */}
                <button
                  onClick={(e) => toggleExpand(e, courseKey)}
                  className="mr-1 p-1 rounded hover:bg-black/5 transition-transform duration-200"
                >
                  {isCourseLoading ? (
                    <div className="w-3 h-3 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <svg 
                      className={`w-3 h-3 text-gray-500 transform ${isCourseExpanded ? 'rotate-90' : 'rotate-0'}`} 
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
                <span className="mr-2 text-lg">📚</span> 
                <span className="truncate">{course.title}</span>
              </div>
              
              {/* ================= LEVEL 2: STAGE (Child) ================= */}
              {isCourseExpanded && hasStages && (
                <div className="ml-3 border-l-2 border-gray-100 pl-1 my-1 space-y-1">
                  {course.stages!.map(stage => {
                    const stageKey = `stage-${stage.id}`;
                    const isStageExpanded = expandedKeys.has(stageKey);
                    const hasLessons = stage.lessons && stage.lessons.length > 0;
                    const isStageLoading = loadingKeys.has(stageKey);

                    return (
                      <div key={stage.id}>
                        <div 
                          onClick={() => onSelect({ type: 'stage', id: stage.id, title: stage.title, parentId: course.id })}
                          className={`group cursor-pointer py-1.5 px-2 rounded flex items-center transition-colors select-none ${
                            isSelected('stage', stage.id)
                            ? 'bg-blue-50 text-blue-700 font-medium' 
                            : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <button
                            onClick={(e) => toggleExpand(e, stageKey)}
                            className="mr-1 p-0.5 rounded hover:bg-black/5 transition-transform duration-200"
                          >
                            {isStageLoading ? (
                              <div className="w-3 h-3 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <svg 
                                className={`w-3 h-3 text-gray-400 transform ${isStageExpanded ? 'rotate-90' : 'rotate-0'}`} 
                                fill="none" viewBox="0 0 24 24" stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            )}
                          </button>
                          <span className="mr-2 text-sm">🔹</span>
                          <span className="text-sm truncate">{stage.title}</span>
                        </div>

                        {/* ================= LEVEL 3: LESSON (Grandchild) ================= */}
                        {isStageExpanded && hasLessons && (
                          <div className="ml-4 border-l border-gray-100 pl-2 my-1 space-y-0.5">
                            {stage.lessons!.map(lesson => (
                              <div 
                                key={lesson.id}
                                onClick={() => onSelect({ type: 'lesson', id: lesson.id, title: lesson.title, parentId: stage.id })}
                                className={`cursor-pointer py-1.5 px-2 rounded flex items-center text-sm transition-colors select-none ${
                                  isSelected('lesson', lesson.id)
                                  ? 'bg-blue-50 text-blue-700 font-medium' 
                                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                                }`}
                              >
                                <span className="mr-2 ml-4">📄</span>
                                <span className="text-xs text-gray-400 mr-1">L{lesson.sort_order ?? lesson.id}</span>
                                <span className="truncate">{lesson.title}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ResourceSidebar;
